import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Edit2, Save, FolderOpen, Trash2 } from 'lucide-react';
import { useDocumentation } from '../context/DocumentationContext';

const Documentation = () => {
  const { cards, addCard, deleteCard } = useDocumentation();
  const navigate = useNavigate();
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardName, setNewCardName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  const handleAddCard = () => {
    if (newCardName.trim()) {
      addCard(newCardName);
      setNewCardName('');
      setIsAddingCard(false);
    }
  };

  const handleDeleteCard = (id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this documentation card?')) {
      deleteCard(id);
    }
  };

  const handleCardClick = (cardId) => {
    navigate(`/documentation/${cardId}`);
  };

  const cardsArray = Object.values(cards);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div>
          <h1 style={{ color: '#1e293b', margin: '0 0 10px 0' }}>Documentation</h1>
          <p style={{ color: '#64748b', marginBottom: '0' }}>Manage your raw data documentation with organized folders and files.</p>
        </div>
        <button
          onClick={() => setIsEditMode(!isEditMode)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: isEditMode ? '#dbeafe' : '#f1f5f9',
            color: isEditMode ? '#1e40af' : '#64748b',
            border: isEditMode ? '1px solid #3b82f6' : '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isEditMode) {
              e.currentTarget.style.background = '#e2e8f0';
              e.currentTarget.style.color = '#1e293b';
            }
          }}
          onMouseLeave={(e) => {
            if (!isEditMode) {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.color = '#64748b';
            }
          }}
        >
          {isEditMode ? (
            <>
              <Save size={18} />
              <span>Exit Edit Mode</span>
            </>
          ) : (
            <>
              <Edit2 size={18} />
              <span>Edit Mode</span>
            </>
          )}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginTop: '40px' }}>
        
        {cardsArray.map(card => (
          <div
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '30px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 20px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {isEditMode && (
              <button
                onClick={(e) => handleDeleteCard(card.id, e)}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.color = '#dc2626';
                }}
              >
                <Trash2 size={16} />
              </button>
            )}
            <FolderOpen size={64} color="#3b82f6" style={{ marginBottom: '20px' }} />
            <h3 style={{ color: '#1e293b', margin: '0 0 10px 0', fontSize: '20px' }}>{card.name}</h3>
            <p style={{ color: '#94a3b8', margin: 0, fontSize: '14px' }}>Click to open</p>
          </div>
        ))}

        {isEditMode && (
          <div
            style={{
              backgroundColor: 'white',
              border: '2px dashed #cbd5e1',
              borderRadius: '12px',
              padding: '40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minHeight: '200px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.backgroundColor = '#f0f9ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.backgroundColor = 'white';
            }}
            onClick={() => setIsAddingCard(true)}
          >
            <Plus size={48} color="#94a3b8" style={{ marginBottom: '15px' }} />
            <h3 style={{ color: '#475569', margin: '0 0 10px 0' }}>Add New Documentation</h3>
            <p style={{ color: '#94a3b8', margin: 0, textAlign: 'center' }}>Click to create a new documentation card</p>
          </div>
        )}
      </div>

      {isAddingCard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Create New Documentation Card</h3>
              <button
                onClick={() => { setIsAddingCard(false); setNewCardName(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#475569', fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                Card Name
              </label>
              <input
                type="text"
                value={newCardName}
                onChange={(e) => setNewCardName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCard()}
                placeholder="e.g., Real Estate Data"
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setIsAddingCard(false); setNewCardName(''); }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f5f9',
                  color: '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddCard}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Create Card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentation;
